/**
 * The states that are NOT a report and NOT an absence.
 *
 * A read failure, a generation refusal, a report newer than this build, and a
 * stored body that fails its own contract all used to render as the same screen
 * a founder sees before they have ever generated anything — with a Generate
 * button under it. Two of those four are fixed by reloading or by support, and
 * pressing Generate spends a monthly run to reproduce a report that already
 * exists.
 *
 * Each state says what happened, whose problem it is, and what to do. None of
 * them shows an internal message: a Supabase or provider error can carry a
 * request URL, and a request URL here carries the query text.
 */

import React from 'react';
import { Text, View } from 'react-native';
import { AlertTriangle, RefreshCw, ShieldAlert } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { REPORT_READ_MESSAGES } from '@/lib/strategic-analysis/failure-messages';

export type AnalysisProblemKind =
  | 'read_error'
  | 'generation_error'
  | 'unsupported_version'
  | 'malformed'
  | 'legacy_report';

interface AnalysisProblemCardProps {
  kind: AnalysisProblemKind;
  /** A safe, already-mapped sentence. Never an upstream message. */
  message?: string;
  /** Present only when retrying is the action that helps. */
  onRetry?: () => void;
  retryLabel?: string;
}

const TITLES: Record<AnalysisProblemKind, string> = {
  read_error: 'This analysis could not be loaded',
  generation_error: 'This analysis could not be started',
  unsupported_version: 'This report needs a newer version of Inverio',
  malformed: 'This report could not be displayed',
  legacy_report: 'This report predates the current evidence contract',
};

export function AnalysisProblemCard({
  kind,
  message,
  onRetry,
  retryLabel = 'Try again',
}: AnalysisProblemCardProps) {
  const colors = useThemeColors();

  // A CORRUPTION IS NOT A WARNING. The two version problems are amber — the
  // report exists and is intact, this build just cannot read it — while a read
  // or generation failure is the error colour, because something did not work.
  const isVersionProblem = kind === 'unsupported_version' || kind === 'malformed' ||
    kind === 'legacy_report';
  const accent = isVersionProblem ? colors.amber.main : colors.error;
  const body = message ??
    (kind === 'unsupported_version'
      ? REPORT_READ_MESSAGES.unsupported_version
      : kind === 'malformed'
      ? REPORT_READ_MESSAGES.malformed
      : kind === 'legacy_report'
      ? REPORT_READ_MESSAGES.legacy_report
      : 'Something went wrong. Try again.');

  return (
    <View
      accessibilityRole="summary"
      accessibilityLabel={TITLES[kind]}
      className="rounded-2xl p-4 gap-2"
      style={{
        backgroundColor: colors.surface.primary,
        borderWidth: 1,
        borderColor: accent,
        overflow: 'hidden',
      }}
    >
      <View className="flex-row items-center gap-2">
        <View className="shrink-0">
          {isVersionProblem
            ? <ShieldAlert size={16} color={accent} />
            : <AlertTriangle size={16} color={accent} />}
        </View>
        <Text
          numberOfLines={2}
          className="flex-1 text-[14px] font-semibold"
          style={{ color: colors.text.primary }}
        >
          {TITLES[kind]}
        </Text>
      </View>

      <Text className="text-[13px] leading-5" style={{ color: colors.text.secondary }}>
        {body}
      </Text>

      {/* Offered only where retrying is the action that helps. A retry button on
          a malformed stored report invites the reader to keep pressing something
          that cannot work. */}
      {onRetry && (
        <View className="rounded-xl overflow-hidden self-start" style={{ height: 40, backgroundColor: accent }}>
          <AnimatedPressable
            onPress={onRetry}
            accessibilityRole="button"
            accessibilityLabel={retryLabel}
            className="flex-row items-center justify-center gap-2 px-4"
            style={{ flex: 1 }}
          >
            <RefreshCw size={14} color={colors.text.inverse} />
            <Text numberOfLines={1} className="text-[13px] font-semibold" style={{ color: colors.text.inverse }}>
              {retryLabel}
            </Text>
          </AnimatedPressable>
        </View>
      )}
    </View>
  );
}
